import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import SponsorModal from '../components/SponsorModal';

const tiers = [
  {
    name: 'Founding',
    key: 'founding',
    price: '$500',
    accent: 'teal',
    perks: [
      'Film Pre-Roll on 1 film',
      'Logo on site and in closing credits',
      'Banner ad during festival',
      'Post-festival performance report',
    ],
  },
  {
    name: 'Community',
    key: 'community',
    price: '$1,500',
    accent: 'teal',
    perks: [
      'Everything in Founding, plus:',
      'Film Pre-Rolls on 5 films',
      'Ad placement during 2 Q&A sessions',
      'Ads shown during post-festival replays',
      'Pause ad overlay',
      'Sponsored break-out session mention',
      'Social media posts',
      'Film DNA audience data report',
    ],
  },
  {
    name: 'Category',
    key: 'category',
    price: 'Custom',
    accent: 'gold',
    featured: true,
    contactOnly: true,
    perks: [
      'Everything in Community, plus:',
      'Film Pre-Rolls on 15+ films across your category',
      'Ad placement during 10 Q&A sessions',
      'Ads shown during all post-festival replays',
      '"Best [Category] presented by [Brand]"',
      '17 categories available',
      'Group buying units across your category screenings',
      'Named break-out session sponsorship',
      'Live shoutouts in Q&As',
      'Logo on award certificates',
    ],
  },
  {
    name: 'Presenting',
    key: 'presenting',
    price: 'Custom',
    accent: 'orange',
    featured: true,
    limit: 'Limited to 1 sponsor',
    contactOnly: true,
    perks: [
      'Everything in Category, plus:',
      'Film Pre-Rolls on ALL films',
      'Ad placement during ALL Q&A sessions',
      'Ads shown during ALL replays for the full 7 days',
      '"Synema Interactive Film Festival, Presented by [Your Brand]"',
      'Opening and closing ceremony branding',
      'Dedicated sponsor spotlight segment',
      'All award categories co-branded',
      'Keynote or guest speaker slot',
      'Year-round banner on SynemaFest.com',
      'Custom Film DNA audience report',
      'Dedicated account manager',
    ],
  },
];

const adFormats = [
  {
    name: 'Film Pre-Rolls',
    desc: 'Your brand plays before each watch party screening. High-attention, unskippable, communal viewing context.',
  },
  {
    name: 'Group Buying Units',
    desc: 'Interactive ad format exclusive to Mindi.TV. Viewers engage collectively with your offer — creating social proof and virality in real time.',
  },
  {
    name: 'Banner & Logo Placement',
    desc: 'Festival-wide logo presence across screening rooms, schedule pages, emails, and social channels.',
  },
  {
    name: 'Sponsored Breakout Sessions',
    desc: 'Put your brand behind a craft or industry breakout session. Reach filmmakers and creators directly.',
  },
];

const accentClasses = {
  teal: { border: 'border-teal/20', bar: 'bg-gradient-to-r from-teal to-transparent', dot: 'bg-teal', price: 'text-teal', btn: 'bg-teal' },
  gold: { border: 'border-gold/30', bar: 'bg-gradient-to-r from-gold to-transparent', dot: 'bg-gold', price: 'text-gold', btn: 'bg-gold' },
  orange: { border: 'border-orange/30', bar: 'bg-gradient-to-r from-orange to-transparent', dot: 'bg-orange', price: 'text-orange', btn: 'btn-gradient' },
};

const Sponsors = () => {
  const [searchParams] = useSearchParams();
  const [modalTier, setModalTier] = useState(null);

  const isSuccess = searchParams.get('success') === 'true';

  if (isSuccess) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6">
        <div className="max-w-xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-20 h-20 rounded-full bg-gold/20 border border-gold/30 flex items-center justify-center mx-auto mb-8">
              <svg className="w-10 h-10 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="font-display text-[clamp(2.5rem,5vw,4rem)] tracking-[0.04em] leading-none text-white mb-6">
              Welcome aboard.
            </h1>
            <p className="text-[1rem] font-light leading-[1.8] text-white/[0.65] mb-4">
              Your sponsorship is confirmed! Check your email for your dashboard login — upload your creative assets and we'll take care of the rest.
            </p>
            <p className="text-[0.8rem] text-muted">
              Don't see the email? Check your spam folder for a message from synemafest@gmail.com.
            </p>
            <a href="/sponsor-dashboard" className="inline-block mt-6 bg-gold text-dark font-body text-[0.78rem] font-semibold tracking-[0.08em] uppercase py-3 px-8 rounded-sm hover:opacity-90 transition-all no-underline">
              Go to Sponsor Dashboard
            </a>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="font-mono text-[0.7rem] tracking-[0.25em] uppercase text-gold mb-4">
            Sponsor Synema Film Fest
          </div>
          <h1 className="font-display text-[clamp(2.5rem,5vw,4rem)] tracking-[0.04em] leading-none text-white mb-6">
            Reach the audience that actually watches.
          </h1>
          <p className="text-[1rem] font-light leading-[1.8] text-white/[0.65] max-w-[640px] mb-16">
            Synema Film Fest reaches a global audience of engaged cinephiles who show up to watch, not to scroll. During the festival, your brand appears across dozens of films in front of thousands of viewers in communal screening rooms — where ads aren't skipped, they're part of the experience.
          </p>
        </motion.div>

        {/* Sponsorship Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
          {tiers.map((tier, i) => {
            const a = accentClasses[tier.accent];
            return (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * i }}
                className={`bg-dark2 border ${a.border} rounded p-8 relative overflow-hidden flex flex-col`}
              >
                <div className={`absolute top-0 left-0 right-0 h-[2px] ${a.bar}`} />
                <div className="h-6">
                  {tier.limit && (
                    <div className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-orange">
                      {tier.limit}
                    </div>
                  )}
                </div>
                <div className={`font-display text-4xl mb-1 ${a.price}`}>
                  {tier.contactOnly ? (
                    <a href="mailto:synemafest@gmail.com" className={`${a.price} no-underline hover:opacity-80 transition-opacity`}>
                      Interested? Let's Chat
                    </a>
                  ) : tier.price}
                </div>
                <div className="font-display text-xl tracking-[0.05em] text-white mb-1">
                  {tier.name.toUpperCase()}
                </div>
                <div className="h-px bg-white/[0.06] my-4" />
                <ul className="space-y-3 mb-8 flex-1">
                  {tier.perks.map((perk, j) => (
                    <li key={j} className="flex gap-3 items-start text-[0.85rem] font-light text-white/60 leading-relaxed">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2 ${a.dot}`} />
                      {perk}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setModalTier(tier.key)}
                  className={`block w-full ${a.btn} text-dark font-body text-[0.78rem] font-semibold tracking-[0.08em] uppercase py-3 px-6 rounded-sm hover:opacity-90 transition-all hover:-translate-y-[1px] text-center cursor-pointer border-none`}
                >
                  {(tier.key === 'founding' || tier.key === 'community')
                    ? `Get ${tier.name} — ${tier.price}`
                    : `Inquire — ${tier.name}`}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Ad Formats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <h2 className="font-display text-2xl tracking-[0.05em] text-white mb-6">AD FORMATS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {adFormats.map((format, i) => (
              <div key={i} className="bg-dark2 border border-white/[0.06] rounded p-6">
                <h3 className="font-display text-lg text-gold mb-2">{format.name.toUpperCase()}</h3>
                <p className="text-[0.85rem] font-light text-white/50 leading-relaxed">{format.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Group Buying Explainer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="bg-dark2 border border-gold/20 rounded p-8">
            <h2 className="font-display text-2xl tracking-[0.05em] text-gold mb-4">WHAT IS A GROUP BUYING UNIT?</h2>
            <p className="text-[0.95rem] font-light text-white/60 leading-[1.8] mb-4">
              Group buying is an ad format exclusive to Mindi.TV. During a watch party, an interactive offer appears — and instead of one viewer clicking, the entire audience can opt in together. When enough viewers engage, the deal activates for everyone.
            </p>
            <p className="text-[0.95rem] font-light text-white/60 leading-[1.8]">
              This creates social proof in real time, drives higher engagement than traditional ads, and gives sponsors measurable community-level data. Think of it as a commercial that the audience actually participates in.
            </p>
          </div>
        </motion.div>

        {/* Film DNA Targeting */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="bg-dark2 border border-teal/20 rounded p-8">
            <h2 className="font-display text-2xl tracking-[0.05em] text-teal mb-4">FILM DNA TARGETING</h2>
            <p className="text-[0.95rem] font-light text-white/60 leading-[1.8]">
              Mindi.TV's Film DNA system understands viewer preferences at a granular level — genre affinity, mood preferences, engagement patterns, and watch behavior. As a sponsor, you can target your ads to specific audience segments based on what they actually watch and engage with, not just demographics. Reach the right viewers at the right moment.
            </p>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <p className="text-[0.95rem] font-light text-white/50">
            Questions about sponsorship? Email{' '}
            <a href="mailto:synemafest@gmail.com" className="text-teal font-medium">synemafest@gmail.com</a>
          </p>
        </motion.div>
      </div>

      <SponsorModal
        tier={modalTier}
        isOpen={modalTier !== null}
        onClose={() => setModalTier(null)}
      />
    </div>
  );
};

export default Sponsors;
